import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { AuthType } from "src/common/types/auth";
import { PublicGet, PublicPost, StringToB64 } from "libs/services";
import { USER } from "libs/config";
import { setLoading } from "./loader";
import { openToast } from "./toast";
import moment from "moment";
import translate from "src/common/libs/locale";
import { capitalizeFirstLetter } from "src/common/libs/utils/dataUtils";
import { reportLog } from "src/common/libs/utils/logs";

interface AuthState {
  token: {
    token: string;
    refreshToken: string;
  };
}

export const register = createAsyncThunk(
  "auth/register",
  async (
    {
      email,
      phone,
      username,
      password,
      firstName,
      lastName,
    }: {
      email: string;
      phone: string;
      username: string;
      password: string;
      firstName: string;
      lastName: string;
    },
    thunkAPI
  ) => {
    const encodedCredentials = StringToB64(`${username}:${password}`);
    const headers = { Authorization: encodedCredentials };
    try {
      // thunkAPI.dispatch(setLoading(true));
      const date = moment().unix();
      const response = await PublicPost(
        USER.register,
        null,
        {
          email,
          phone,
          username,
          password,
          role: 4,
          is_active: true,
          created: date,
          updated: date,
          last_active: date,
          first_name: firstName,
          last_name: lastName,
        },
        headers
      );
      return response.data.payload;
    } catch (error) {
      reportLog({
        error: error,
        location: "features/register->register",
        body: {
          email,
          phone,
          username,
          firstName,
          lastName,
        },
      });
      thunkAPI.dispatch(
        openToast({ message: error.response?.data?.message || error.message })
      );
      throw error;
    } finally {
      // thunkAPI.dispatch(setLoading(false));
    }
  }
);

export const registerCheck = createAsyncThunk(
  "auth/registerCheck",
  async (
    {
      email,
      phone,
      username,
      showError = true,
    }: {
      email?: string;
      phone?: string;
      username?: string;
      showError?: boolean;
    },
    thunkAPI
  ) => {
    try {
      const params = `?username=${username}&phone=${phone}&email=${email}`;
      const response = await PublicGet(`${USER.registerCheck}${params}`);
      return response.data;
    } catch (error) {
      reportLog({
        error: error,
        location: "features/register->registerCheck",
        body: {
          email,
          phone,
          username,
        },
      });
      if (!error?.response?.data?.payload) {
        return false;
      }
      const fields = Object.entries(error?.response?.data?.payload)
        .filter((it) => it[1])
        .map((key) => capitalizeFirstLetter(key[0]))
        .join(", ");
      showError &&
        thunkAPI.dispatch(
          openToast({ message: translate("registerExists", { field: fields }) })
        );
      return true;
    } finally {
    }
  }
);

export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async (
    {
      password,
      token,
      showMessage = false,
    }: {
      password: string;
      token: string;
      showMessage?: boolean;
    },
    thunkAPI
  ) => {
    try {
      const response = await PublicPost(USER.changePassword, null, {
        password,
        token,
      });
      showMessage &&
        response.data?.status &&
        thunkAPI.dispatch(
          openToast({ message: translate("passwordChanged"), type: "success" })
        );
      return response.data;
    } catch (error) {
      reportLog({
        error: error,
        location: "features/register->changePassword",
        body: {},
      });
      thunkAPI.dispatch(
        openToast({ message: error.response?.data?.message || error.message })
      );
      return true;
    } finally {
    }
  }
);

const initialState: AuthState = {
  token: null,
};

export const registerSlice = createSlice({
  name: "register",
  initialState,
  reducers: {
    setToken: (state, action: PayloadAction<AuthType>) => {
      state.token = {
        token: action.payload?.t,
        refreshToken: action.payload?.rt,
      };
    },
    clearToken: (state) => {
      state.token = null;
    },
    clearData: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(
      register.fulfilled,
      (state, action: PayloadAction<AuthType>) => {
        state.token = {
          token: action.payload?.t,
          refreshToken: action.payload?.rt,
        };
      }
    );
  },
});

export const { setToken, clearToken, clearData } = registerSlice.actions;

export default registerSlice.reducer;
